import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Youtube, Check, X, RefreshCw, Loader2, AlertCircle, ExternalLink, KeyRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { youtubeApi, facilitiesApi, queryKeys } from '@/lib/api';
import { useAuth } from '@/store/auth';

// ─── Stream Keys subsection ───────────────────────────────────────────────────
// Greyed out unless connected; if keys already exist the button locks behind a
// confirmation checkbox and warns on press to prevent accidental overwrites/dupes.
function StreamKeysSection({ facilityId, facilityName, isConnected }: { facilityId: string; facilityName: string; isConnected: boolean }) {
  const queryClient = useQueryClient();
  const [unlocked, setUnlocked] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: queryKeys.youtube.streamKeys(facilityId),
    queryFn: () => youtubeApi.getStreamKeyStatus(facilityId),
    enabled: isConnected,
  });

  const createMutation = useMutation({
    mutationFn: () => youtubeApi.createStreamKeys(facilityId),
    onSuccess: (res) => {
      setResultMsg(`Done — created ${res.created}, found ${res.found}${res.missing ? `, ${res.missing} still missing` : ''}.`);
      setErrorMsg(null);
      setUnlocked(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.streamKeys(facilityId) });
      setTimeout(() => setResultMsg(null), 6000);
    },
    onError: (err: any) => setErrorMsg(err?.message || 'Failed to create stream keys'),
  });

  const withKey = status?.withKey ?? 0;
  const total = status?.total ?? 0;
  const keysExist = withKey > 0;
  const buttonDisabled = !isConnected || createMutation.isPending || (keysExist && !unlocked);

  const handleCreate = () => {
    if (!isConnected) return;
    const msg = keysExist
      ? `WARNING: ${facilityName} already has ${withKey}/${total} stream keys.\n\nRe-running creates one reusable stream per sheet whose stream isn't matched by title — which can produce DUPLICATE streams on the YouTube channel. Continue?`
      : `Create stream keys for ${facilityName}? This creates one reusable 1080p60 stream per sheet on the connected YouTube channel.`;
    if (!window.confirm(msg)) return;
    createMutation.mutate();
  };

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Stream Keys</p>
      {!isConnected ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">Connect a YouTube channel to create stream keys.</p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isLoading ? 'Checking…'
                : total === 0 ? 'No sheets configured'
                : keysExist ? `${withKey}/${total} sheets have keys`
                : `No keys yet (${total} sheets)`}
            </p>
            <Button size="sm" onClick={handleCreate} disabled={buttonDisabled}>
              {createMutation.isPending
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <KeyRound className="mr-1 h-3.5 w-3.5" />}
              Create Keys
            </Button>
          </div>
          {keysExist && (
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <input
                type="checkbox"
                checked={unlocked}
                onChange={(e) => setUnlocked(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600"
              />
              Keys already exist — tick to allow re-running (may create duplicates)
            </label>
          )}
          {resultMsg && <p className="text-xs text-green-600 dark:text-green-400">{resultMsg}</p>}
          {errorMsg && <p className="text-xs text-red-500">{errorMsg}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Facility YouTube Section ─────────────────────────────────────────────────
function FacilityYouTubeSection({ facilityId, facilityName }: { facilityId: string; facilityName: string }) {
  const queryClient = useQueryClient();
  const [titleTemplate, setTitleTemplate] = useState('');
  const [templateSaved, setTemplateSaved] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  // Credentials
  const { data: credData, isLoading: credLoading, refetch: refetchCreds } = useQuery({
    queryKey: queryKeys.youtube.credentials(facilityId),
    queryFn: () => youtubeApi.getCredentials(facilityId),
  });

  // Rename rule
  const { data: ruleData, isLoading: ruleLoading } = useQuery({
    queryKey: queryKeys.youtube.rule(facilityId),
    queryFn: () => youtubeApi.getRenameRule(facilityId),
  });

  // Sync template from loaded rule
  useEffect(() => {
    if (ruleData?.rule) {
      setTitleTemplate(ruleData.rule.titleTemplate || '');
    }
  }, [ruleData]);

  const disconnectMutation = useMutation({
    mutationFn: () => youtubeApi.deleteCredentials(facilityId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.youtube.credentials(facilityId) }),
  });

  const saveTemplateMutation = useMutation({
    mutationFn: () => {
      const existingRule = ruleData?.rule;
      if (existingRule) {
        return youtubeApi.updateRenameRule(facilityId, { titleTemplate });
      }
      return youtubeApi.saveRenameRule(facilityId, titleTemplate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.rule(facilityId) });
      setTemplateSaved(true);
      setTemplateError(null);
      setTimeout(() => setTemplateSaved(false), 3000);
    },
    onError: (err: any) => setTemplateError(err?.message || 'Failed to save'),
  });

  const handleConnectYouTube = () => {
    const authUrl = `/api/youtube/oauth/authorize?facilityId=${facilityId}`;
    const popup = window.open(
      authUrl,
      'youtube-auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'youtube-oauth-success') {
        window.removeEventListener('message', listener);
        popup?.close();
        refetchCreds();
        queryClient.invalidateQueries({ queryKey: queryKeys.youtube.credentials(facilityId) });
      }
    };
    window.addEventListener('message', listener);

    // Also poll in case postMessage doesn't fire
    const interval = setInterval(() => {
      if (popup?.closed) {
        clearInterval(interval);
        window.removeEventListener('message', listener);
        refetchCreds();
      }
    }, 1000);
  };

  const credentials = credData;
  const isConnected = credentials?.connected ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Youtube className="h-5 w-5 text-red-500" />
          {facilityName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* OAuth Status */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Connection Status</p>
          {credLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : isConnected ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                  <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                </span>
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">Connected</p>
                  {credData?.channelName && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Channel: {credData.channelName}
                    </p>
                  )}
                  {credData?.tokenExpiresAt && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Token expires: {new Date(credData.tokenExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { if (confirm('Disconnect YouTube for this facility?')) disconnectMutation.mutate(); }}
                disabled={disconnectMutation.isPending}
                className="text-red-600 hover:border-red-300 hover:bg-red-50 dark:text-red-400"
              >
                <X className="mr-1 h-3.5 w-3.5" />
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                  <Youtube className="h-3.5 w-3.5 text-gray-400" />
                </span>
                Not connected
              </div>
              <Button size="sm" onClick={handleConnectYouTube}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                Connect YouTube
              </Button>
            </div>
          )}
        </div>

        {/* Title Template */}
        <div>
          <p className="mb-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Stream Title Template</p>
          {ruleLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={titleTemplate}
                  onChange={e => setTitleTemplate(e.target.value)}
                  placeholder="{team1} vs {team2} - Sheet {sheet}"
                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (!titleTemplate.trim()) { setTemplateError('Template cannot be empty'); return; }
                    saveTemplateMutation.mutate();
                  }}
                  disabled={saveTemplateMutation.isPending}
                >
                  {saveTemplateMutation.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : templateSaved
                    ? <Check className="h-3.5 w-3.5 text-green-500" />
                    : 'Save'}
                </Button>
              </div>
              {templateError && <p className="text-xs text-red-500">{templateError}</p>}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Available variables:{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{team1}'}</code>{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{team2}'}</code>{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{sheet}'}</code>{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{event}'}</code>{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">{'{date}'}</code>
              </p>
            </div>
          )}
        </div>

        {/* Stream Keys */}
        <StreamKeysSection facilityId={facilityId} facilityName={facilityName} isConnected={isConnected} />
      </CardContent>
    </Card>
  );
}

// ─── Rename Logs Tab ──────────────────────────────────────────────────────────
function RenameLogsTab() {
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFacility, setFilterFacility] = useState('');

  const queryParams = {
    ...(filterStatus ? { status: filterStatus } : {}),
    ...(filterFacility ? { facilityId: filterFacility } : {}),
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.youtube.logs(queryParams),
    queryFn: () => youtubeApi.getRenameLogs(queryParams),
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => youtubeApi.retryRename(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.all });
    },
  });

  const logs = data?.logs || [];

  const statusClass = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      case 'pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Facility ID</label>
          <input
            type="text"
            value={filterFacility}
            onChange={e => setFilterFacility(e.target.value)}
            placeholder="e.g. fac1"
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="mr-1 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          Failed to load rename logs
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Facility</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Event</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Teams</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Original Title</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">New Title</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Created</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No rename log entries
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-xs text-gray-500">{log.facilityId}</td>
                    <td className="px-4 py-3 font-medium">{log.schedule?.eventName || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {log.schedule?.team1Name && log.schedule?.team2Name
                        ? `${log.schedule.team1Name} vs ${log.schedule.team2Name}`
                        : '—'}
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      <span className="block truncate">{log.originalTitle || '—'}</span>
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs">
                      <span className="block truncate">{log.newTitle || '—'}</span>
                      {log.errorMessage && (
                        <span className="block truncate text-red-500">{log.errorMessage}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {log.status === 'failed' && (
                        <button
                          onClick={() => retryMutation.mutate(log.id)}
                          disabled={retryMutation.isPending}
                          title="Retry"
                          className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
type Tab = 'connections' | 'logs';

export default function YouTubePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState<Tab>('connections');
  const [selectedFacility, setSelectedFacility] = useState<string | null>(
    user?.role === 'facility' ? (user.facilityId ?? null) : null
  );

  const { data: facilitiesData, isLoading: facilitiesLoading } = useQuery({
    queryKey: queryKeys.facilities.list(),
    queryFn: () => facilitiesApi.list({ limit: 100 }),
    enabled: isAdmin,
  });

  const facilities = (facilitiesData?.facilities || []).sort((a, b) => a.name.localeCompare(b.name));

  // For facility users, auto-select their facility
  const facilitiesToShow = isAdmin
    ? (selectedFacility
        ? facilities.filter(f => f.facilityId === selectedFacility)
        : facilities)
    : facilities.filter(f => f.facilityId === user?.facilityId);

  const tabs = [
    { key: 'connections' as Tab, label: 'YouTube Connections' },
    { key: 'logs' as Tab, label: 'Rename Logs' },
  ];

  // Non-admin users only see their own facility; no logs tab
  const visibleTabs = isAdmin ? tabs : [tabs[0]];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Youtube className="h-7 w-7 text-red-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">YouTube Integration</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Connect YouTube channels and configure automatic stream title renaming.
          </p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6">
          {visibleTabs.map(t => t && (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? 'border-red-500 text-red-600 dark:border-red-400 dark:text-red-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'connections' && (
        <div className="space-y-4">
          {/* Facility selector for admins */}
          {isAdmin && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Filter by facility:</label>
              <select
                value={selectedFacility || ''}
                onChange={e => setSelectedFacility(e.target.value || null)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">All Facilities</option>
                {facilities.map(f => (
                  <option key={f.facilityId} value={f.facilityId}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {facilitiesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : facilitiesToShow.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No facilities found.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {facilitiesToShow.map(f => (
                <FacilityYouTubeSection
                  key={f.facilityId}
                  facilityId={f.facilityId}
                  facilityName={f.name}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && isAdmin && <RenameLogsTab />}
    </div>
  );
}
